import { useMemo } from 'react';
import type { SubscriptionPlan } from '../api/viewSubscriptionPlanApiService';

// ============================================================================
// Constants
// ============================================================================

export const DURATION_TYPE_LABELS: Record<
  SubscriptionPlan['durationType'],
  string
> = {
  MONTHLY: 'Miesięczny',
  YEARLY: 'Roczny',
} as const;

export const BILLING_CYCLE_LABELS: Record<
  SubscriptionPlan['billingCycle'],
  string
> = {
  MONTHLY: 'Miesięczny',
  QUARTERLY: 'Kwartalny',
  YEARLY: 'Roczny',
} as const;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Formats an ISO date string to Polish locale format
 * Format: YYYY/MM/DD, HH:MM:SS
 */
export const formatDate = (isoString: string | null | undefined): string => {
  if (!isoString) {
    return '—';
  }
  const date = new Date(isoString);
  const formatter = new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  const hour = parts.find((p) => p.type === 'hour')?.value;
  const minute = parts.find((p) => p.type === 'minute')?.value;
  const second = parts.find((p) => p.type === 'second')?.value;
  return `${year}/${month}/${day}, ${hour}:${minute}:${second}`;
};

/**
 * Extracts a user name from a value that can be a string or user object
 */
const extractUserName = (
  value:
    | string
    | { name?: string; email?: string; [key: string]: unknown }
    | null
    | undefined
): string => {
  if (value === null || value === undefined) {
    return '—';
  }

  // If it's already a string, return it
  if (typeof value === 'string') {
    return value.trim() ? value : '—';
  }

  // If it's an object, try to extract name or email
  if (typeof value === 'object') {
    if (value.name && typeof value.name === 'string') {
      return value.name.trim() ? value.name : '—';
    }
    if (value.email && typeof value.email === 'string') {
      return value.email.trim() ? value.email : '—';
    }
  }

  return '—';
};

/**
 * Returns the value if it exists and is not empty, otherwise returns '—'
 */
export const orDash = (value: string | null | undefined): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  // Convert to string if it's not already a string
  const stringValue = typeof value === 'string' ? value : String(value);
  return stringValue.trim() ? stringValue : '—';
};

/**
 * Formats price with space as thousand separator and comma as decimal separator (Polish format)
 */
export const formatPrice = (price: number | null | undefined): string => {
  if (price === null || price === undefined) {
    return '—';
  }
  // Polish locale uses non-breaking space (U+00A0), convert to regular space for consistency
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  })
    .format(price)
    .replace(/\u00A0/g, ' ');
};

/**
 * Formats duration based on duration type
 */
export const formatDuration = (
  duration: number | null | undefined,
  durationType: SubscriptionPlan['durationType'] | null | undefined
): string => {
  if (duration === null || duration === undefined || !durationType) {
    return '—';
  }

  if (durationType === 'MONTHLY') {
    if (duration === 1) {
      return '1 miesiąc';
    }
    if (duration >= 2 && duration <= 4) {
      return `${duration} miesiące`;
    }
    return `${duration} miesiące`;
  } else {
    // YEARLY
    if (duration === 1) {
      return '1 rok';
    }
    if (duration >= 2 && duration <= 4) {
      return `${duration} lata`;
    }
    return `${duration} lat`;
  }
};

/**
 * Gets the duration type label
 */
export const getDurationTypeLabel = (
  durationType: SubscriptionPlan['durationType'] | null | undefined
): string => {
  return durationType ? DURATION_TYPE_LABELS[durationType] : '—';
};

/**
 * Gets the billing cycle label
 */
export const getBillingCycleLabel = (
  billingCycle: SubscriptionPlan['billingCycle'] | null | undefined
): string => {
  return billingCycle ? BILLING_CYCLE_LABELS[billingCycle] : '—';
};

/**
 * Formats number or returns dash
 */
export const formatNumberOrDash = (
  value: number | null | undefined
): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  return String(value);
};

/**
 * Formats "Billed for" text in Polish
 */
export const formatBilledFor = (
  duration: number | null | undefined,
  durationType: SubscriptionPlan['durationType'] | null | undefined
): string => {
  if (duration === null || duration === undefined || !durationType) {
    return '';
  }

  if (durationType === 'MONTHLY') {
    if (duration === 1) {
      return 'Rozliczane za 1 miesiąc';
    }
    if (duration >= 2 && duration <= 4) {
      return `Rozliczane za ${duration} miesiące`;
    }
    return `Rozliczane za ${duration} miesiące`;
  } else {
    // YEARLY
    if (duration === 1) {
      return 'Rozliczane za 1 rok';
    }
    if (duration >= 2 && duration <= 4) {
      return `Rozliczane za ${duration} lata`;
    }
    return `Rozliczane za ${duration} lat`;
  }
};

// ============================================================================
// Hook Return Type
// ============================================================================

export interface UseViewSubscriptionPlanContentReturn {
  // Formatted values - Basic Information
  formattedDescription: string;
  formattedCreatedAt: string;
  formattedUpdatedAt: string;
  formattedCreatedBy: string;
  formattedUpdatedBy: string;

  // Formatted values - Pricing & Billing
  formattedPrice: string;
  formattedDurationType: string;
  formattedDuration: string;
  formattedBillingCycle: string;
  formattedBilledFor: string;
  formattedOffer: string;

  // Formatted values - Usage Limits
  formattedNumberOfUsers: string;
  formattedNumberOfFacilities: string;
  formattedNumberOfQmsDocuments: string;

  // Utility functions
  orDash: (value: string | null | undefined) => string;
}

// ============================================================================
// useViewSubscriptionPlanContent Hook
// ============================================================================

/**
 * Custom hook for managing ViewSubscriptionPlanContent component logic
 * Provides formatted values for plan data display
 */
export const useViewSubscriptionPlanContent = (
  plan: SubscriptionPlan | null
): UseViewSubscriptionPlanContentReturn => {
  // Memoize formatted values to avoid recalculating on every render

  // Basic Information
  const formattedDescription = useMemo(
    () => orDash(plan?.planDescription),
    [plan?.planDescription]
  );

  const formattedCreatedAt = useMemo(
    () => formatDate(plan?.createdAt),
    [plan?.createdAt]
  );

  const formattedUpdatedAt = useMemo(
    () => formatDate(plan?.updatedAt),
    [plan?.updatedAt]
  );

  const formattedCreatedBy = useMemo(
    () => extractUserName(plan?.createdBy),
    [plan?.createdBy]
  );

  const formattedUpdatedBy = useMemo(
    () => extractUserName(plan?.updatedBy),
    [plan?.updatedBy]
  );

  // Pricing & Billing
  const formattedPrice = useMemo(() => formatPrice(plan?.price), [plan?.price]);

  const formattedDurationType = useMemo(
    () => getDurationTypeLabel(plan?.durationType),
    [plan?.durationType]
  );

  const formattedDuration = useMemo(
    () => formatDuration(plan?.duration, plan?.durationType),
    [plan?.duration, plan?.durationType]
  );

  const formattedBillingCycle = useMemo(
    () => getBillingCycleLabel(plan?.billingCycle),
    [plan?.billingCycle]
  );

  const formattedBilledFor = useMemo(
    () => formatBilledFor(plan?.duration, plan?.durationType),
    [plan?.duration, plan?.durationType]
  );

  const formattedOffer = useMemo(
    () =>
      orDash(plan?.offer?.offerName || plan?.offer?.offerId || plan?.offerId),
    [plan?.offer?.offerName, plan?.offer?.offerId, plan?.offerId]
  );

  // Usage Limits
  const formattedNumberOfUsers = useMemo(
    () => formatNumberOrDash(plan?.limits?.numberOfUsers),
    [plan?.limits?.numberOfUsers]
  );

  const formattedNumberOfFacilities = useMemo(
    () => formatNumberOrDash(plan?.limits?.numberOfFacilities),
    [plan?.limits?.numberOfFacilities]
  );

  const formattedNumberOfQmsDocuments = useMemo(
    () => formatNumberOrDash(plan?.limits?.numberOfQmsDocuments),
    [plan?.limits?.numberOfQmsDocuments]
  );

  return {
    // Basic Information
    formattedDescription,
    formattedCreatedAt,
    formattedUpdatedAt,
    formattedCreatedBy,
    formattedUpdatedBy,

    // Pricing & Billing
    formattedPrice,
    formattedDurationType,
    formattedDuration,
    formattedBillingCycle,
    formattedBilledFor,
    formattedOffer,

    // Usage Limits
    formattedNumberOfUsers,
    formattedNumberOfFacilities,
    formattedNumberOfQmsDocuments,

    // Utility functions
    orDash,
  };
};
